import { useState, useEffect, useCallback, useMemo, useReducer, useRef } from "react";
import { SESSION_KEY } from "constants/key";
import { NewQuoteMaker, EstQuoteDetailTire } from "features/car_model/types";
import { findOptionPrice } from "features/mst_store_tire_wheel_filter/hooks/useQuoteWheel";
import {
  CostSettingInfo,
  SetTireProcFeeStorage,
  SetWheelProcFeeStorage,
} from "features/mst_proc_fee/types/proc-fee";
import useInitialDataLoader from "features/mst_proc_fee/hooks/useInitialDataLoader";
import {
  fetchAllMstItemOptions,
  fetchNatMstItemOptions,
} from "features/mst_option/utils/fetchMstItemOptions";
import { useSpecialPrices } from "features/mst_proc_fee/hooks/useSpecialPrices";
import { formatCurrency } from "utils";
import { TireCostSettings } from "features/mst_proc_fee/utils/priceCalculator";
import {
  recalculateWheelPrices,
  recalculateTirePrices,
} from "features/mst_proc_fee/utils/procFeeConfirmHelpers";
import saveCostSettingsToSession from "features/mst_proc_fee/utils/sessionStorageUtils";
import {
  procFeeConfirmReducer,
  getInitialState,
} from "features/mst_proc_fee/reducers/procFeeConfirmReducer";
import { getSessionData } from "util/session_action";
import { parseNumber } from "util/helper";
import { fetchCostPricesForTires } from "features/mst_proc_fee/api/procFeeApi";

/**
 * Custom hook for ProcFeeConfirm with optimized state management
 */
const useProcFeeConfirm = () => {
  const [state, dispatch] = useReducer(procFeeConfirmReducer, getInitialState());

  // Track if it's the initial mount
  const isInitialMount = useRef(true);

  const [newQuoteMaker] = useState<NewQuoteMaker | undefined>(() => {
    const newQuoteMakerRaw = getSessionData<NewQuoteMaker>(SESSION_KEY.NEW_QUOTE_MAKER);
    return newQuoteMakerRaw || undefined;
  });

  /** Avoid option/select running before wheel/tire session has merged into costSetting (prevents duplicate fetches). */
  const [sessionReady, setSessionReady] = useState(false);
  const optionsFetchSeq = useRef(0);
  /** First successful load fetches all modes; later ロックナット変更は nat のみ */
  const procOptionsBootedRef = useRef(false);
  const optionsRef = useRef(state.options);
  optionsRef.current = state.options;

  // Calculate wheel options price
  const wheelOptionsPrice = useMemo(() => {
    const {
      nut_flag,
      nut,
      hub_ring_flag,
      hub_ring,
      lock_nut_flag,
      lock_nut,
      wheel_coating_flag,
      wheel_coating,
    } = state.costSetting;
    const { nat, hub, lockNat, wcoating } = state.options;

    let total = 0;
    if (nut_flag) total += findOptionPrice(nat, nut);
    if (hub_ring_flag) total += findOptionPrice(hub, hub_ring);
    if (lock_nut_flag) total += findOptionPrice(lockNat, lock_nut);
    if (wheel_coating_flag) total += findOptionPrice(wcoating, wheel_coating);

    return total;
  }, [
    state.costSetting.nut_flag,
    state.costSetting.nut,
    state.costSetting.hub_ring_flag,
    state.costSetting.hub_ring,
    state.costSetting.lock_nut_flag,
    state.costSetting.lock_nut,
    state.costSetting.wheel_coating_flag,
    state.costSetting.wheel_coating,
    state.options.nat,
    state.options.hub,
    state.options.lockNat,
    state.options.wcoating,
  ]);

  // Memoized tire cost settings - Following useTireCalculator logic
  const tireCostSettings = useMemo<TireCostSettings>(() => {
    const cs = state.costSetting;
    return {
      replac_labor_cost: parseNumber(cs.replac_labor_cost),
      waste_tire_cost: cs.waste_tire_cost_flag ? parseNumber(cs.waste_tire_cost) : 0,
      rubber_stem_valve_cost: cs.rubber_stem_valve_flag
        ? parseNumber(cs.rubber_stem_valve_cost)
        : 0,
      nitrogen_inflation_cost: cs.nitrogen_inflation_flag
        ? parseNumber(cs.nitrogen_inflation_cost)
        : 0,
      hub_rust_prevention_cost: cs.hub_rust_prevention_flag
        ? parseNumber(cs.hub_rust_prevention_cost)
        : 0,
      tire_puncture_flag: cs.tire_puncture_flag,
      cashback_flag: cs.cashback_flag,
      cashback_period: Number.isNaN(cs.cashback_period) ? 1 : Number(cs.cashback_period),
    };
  }, [
    state.costSetting.replac_labor_cost,
    state.costSetting.waste_tire_cost_flag,
    state.costSetting.waste_tire_cost,
    state.costSetting.rubber_stem_valve_flag,
    state.costSetting.rubber_stem_valve_cost,
    state.costSetting.nitrogen_inflation_flag,
    state.costSetting.nitrogen_inflation_cost,
    state.costSetting.hub_rust_prevention_flag,
    state.costSetting.hub_rust_prevention_cost,
    state.costSetting.tire_puncture_flag,
    state.costSetting.cashback_flag,
    state.costSetting.cashback_period,
  ]);

  useInitialDataLoader({
    onPunctureProtectionLoaded: (data: any) => {
      dispatch({ type: "SET_PUNCTURE_PROTECTION", payload: data });
    },
    onQuoteByListLoaded: (data: any) => {
      dispatch({ type: "SET_QUOTE_BY_LIST", payload: data });
    },
  });

  // Load session storage data on mount (tire and wheel data) and fetch costPriceMap
  useEffect(() => {
    let cancelled = false;
    const loadDataAndFetchCostPrices = async () => {
      try {
        // Load tire session data
        const tireSessionData = getSessionData<SetTireProcFeeStorage>(SESSION_KEY.SELECTED_TIRE);
        if (tireSessionData) {
          dispatch({ type: "LOAD_FROM_TIRE_SESSION", payload: tireSessionData });
        }

        // Load wheel session data
        const wheelSessionData = getSessionData<SetWheelProcFeeStorage>(SESSION_KEY.SELECTED_WHEEL);
        if (wheelSessionData) {
          dispatch({ type: "LOAD_FROM_WHEEL_SESSION", payload: wheelSessionData });

          // Collect all items (wheels and tires) that need cost_price.
          // Upstream screens normalize the trigger flag onto per-item price_setting_wheel:
          //   - Wheels: wheel.price_setting_wheel (set by useQuoteWheel from store-level rules)
          //   - Tires:  originalTireData.price_setting_wheel (set by tire-list hooks from store.price_setting_wheel)
          const allItems: Array<{ item_cd: string }> = [];

          if (wheelSessionData.wheels_detail) {
            wheelSessionData.wheels_detail.forEach((wheel) => {
              if (wheel.price_setting_wheel !== undefined && wheel.price_setting_wheel !== 0) {
                allItems.push({ item_cd: wheel.item_cd });
              }
            });
          }

          if (tireSessionData?.tbl_quote_detail_tire) {
            tireSessionData.tbl_quote_detail_tire.forEach((tire) => {
              if (
                tire.originalTireData?.price_setting_wheel !== undefined &&
                tire.originalTireData.price_setting_wheel !== 0
              ) {
                allItems.push({ item_cd: tire.originalTireData.item_cd });
              }
            });
          }

          // Fetch costPriceMap from API if there are items that need it
          const store_cd = localStorage.getItem("shop_cd");
          let costPriceMap = wheelSessionData.cost_price_map || {};

          if (store_cd && allItems.length > 0) {
            try {
              const fetchedMap = await fetchCostPricesForTires(allItems, store_cd);
              // Merge fetched map with existing map (fetched takes precedence)
              costPriceMap = { ...costPriceMap, ...fetchedMap };
            } catch (error) {
              // Silently handle error, use existing costPriceMap from session
              if (error instanceof Error) {
                // eslint-disable-next-line no-console
                console.error("Error fetching cost prices:", error.message);
              }
            }
          }

          // Set costPriceMap in state
          if (Object.keys(costPriceMap).length > 0) {
            dispatch({ type: "SET_COST_PRICE_MAP", payload: costPriceMap });
          }
        }
      } catch (error) {
        // Silently handle error
        if (error instanceof Error) {
          // eslint-disable-next-line no-console
          console.error("Error loading session data:", error.message);
        }
      } finally {
        if (!cancelled) {
          setSessionReady(true);
        }
      }
    };

    loadDataAndFetchCostPrices();
    return () => {
      cancelled = true;
    };
  }, []);

  // Single path for item/option/select (per mode) — after session hydration; refetch when lock nut changes
  useEffect(() => {
    if (!sessionReady) {
      return undefined;
    }

    optionsFetchSeq.current += 1;
    const seq = optionsFetchSeq.current;
    let cancelled = false;

    const run = async () => {
      try {
        const fastenersInput = {
          lock_nut_flag: state.costSetting.lock_nut_flag,
          lock_nut: state.costSetting.lock_nut,
        };

        if (!procOptionsBootedRef.current) {
          const list = await fetchAllMstItemOptions(newQuoteMaker, fastenersInput);
          if (cancelled || seq !== optionsFetchSeq.current) return;
          procOptionsBootedRef.current = true;
          dispatch({
            type: "SET_OPTIONS",
            payload: {
              nat: list.nat,
              lockNat: list.locknat,
              hub: list.hub,
              wcoating: list.wcoating,
            },
          });
          return;
        }

        const { lockNat } = optionsRef.current;
        const nat = await fetchNatMstItemOptions(newQuoteMaker, fastenersInput, lockNat);
        if (cancelled || seq !== optionsFetchSeq.current) return;
        dispatch({ type: "SET_OPTIONS", payload: { nat } });
      } catch {
        // Silently handle error
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    sessionReady,
    newQuoteMaker,
    state.costSetting.lock_nut,
    state.costSetting.lock_nut_flag,
  ]);

  // Callback to update special prices
  const handleSpecialPriceUpdate = useCallback(
    (combinationIndex: number, tireIndex: number, price_tire: number, price_wheel: number) => {
      dispatch({
        type: "UPDATE_SPECIAL_PRICES",
        payload: { combinationIndex, tireIndex, price_tire, price_wheel },
      });
    },
    [],
  );

  // Fetch special prices when wheelTireCombinations are loaded
  useSpecialPrices(state.wheelTireCombinations, handleSpecialPriceUpdate);

  // Recalculate prices when cost settings change
  useEffect(() => {
    // Skip on initial mount when combinations are empty
    if (isInitialMount.current || state.wheelTireCombinations.length === 0) {
      isInitialMount.current = false;
      return;
    }

    // Recalculate wheel prices with new options price
    let updatedCombinations = recalculateWheelPrices(
      state.wheelTireCombinations,
      wheelOptionsPrice,
      state.costPriceMap,
    );

    // Recalculate tire prices with new cost settings
    updatedCombinations = recalculateTirePrices(
      updatedCombinations,
      tireCostSettings,
      state.punctureProtection,
      state.costPriceMap,
    );

    dispatch({ type: "UPDATE_TIRE_COMBINATIONS", payload: updatedCombinations });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wheelOptionsPrice, tireCostSettings, state.punctureProtection, state.costPriceMap]);

  // Save costSetting to session storage when it changes
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      return;
    }

    saveCostSettingsToSession(state.costSetting);
  }, [state.costSetting]);

  // Memoized handlers
  const setCustomerInfo = useCallback(
    (info: {
      customer_name?: string;
      customer_tel_no?: string;
      quote_by?: string;
      exp_date?: string;
    }) => {
      dispatch({ type: "SET_CUSTOMER_INFO", payload: info });
    },
    [],
  );

  const setCostSetting = useCallback((info: Partial<CostSettingInfo>) => {
    dispatch({ type: "SET_COST_SETTING", payload: info });
  }, []);

  const updateWheelTireDetail = useCallback(
    (
      combinationIndex: number,
      tireIndex: number,
      key: keyof EstQuoteDetailTire,
      value: unknown,
    ) => {
      dispatch({
        type: "UPDATE_WHEEL_TIRE_DETAIL",
        payload: { combinationIndex, tireIndex, key, value },
      });
    },
    [],
  );

  const updateWheelRemarks = useCallback((combinationIndex: number, remarks: string) => {
    dispatch({
      type: "UPDATE_WHEEL_REMARKS",
      payload: { combinationIndex, remarks },
    });
  }, []);

  const handleSelectClear = useCallback(
    (type: "select" | "clear") => {
      if (type === "select") {
        dispatch({
          type: "SELECT_ALL_OPTIONS",
          payload: state.options,
        });
      } else {
        dispatch({ type: "CLEAR_ALL_OPTIONS" });
      }
    },
    [state.options],
  );

  const handleSelectAllTires = useCallback(() => {
    dispatch({ type: "SELECT_ALL_TIRES" });
  }, []);

  const handleClearAllTires = useCallback(() => {
    dispatch({ type: "CLEAR_ALL_TIRES" });
  }, []);

  // Memoized select options for autocomplete
  const selectOptions = useMemo(
    () => ({
      nat: (state.options?.nat || []).map((el: any) => ({
        label: `${el.option_name}\u00A0\u00A0${formatCurrency(el.sale_price || 0)}円`,
        value: el.option_cd || String(el.id),
      })),
      lockNat: (state.options?.lockNat || []).map((el: any) => ({
        label: `${el.option_name}\u00A0\u00A0${formatCurrency(el.sale_price || 0)}円`,
        value: el.option_cd || `${el.id}`,
      })),
      hub: (state.options?.hub || []).map((el: any) => ({
        label: `${el.option_name}\u00A0\u00A0${formatCurrency(el.sale_price || 0)}円`,
        value: el.option_cd || `${el.id}`,
      })),
      wcoating: (state.options?.wcoating || []).map((el: any) => ({
        label: `${el.option_name}\u00A0\u00A0${formatCurrency(el.sale_price || 0)}円`,
        value: el.option_cd || `${el.id}`,
      })),
    }),
    [state.options?.nat, state.options?.lockNat, state.options?.hub, state.options?.wcoating],
  );

  // Check if any tire has quantity !== 4 to disable puncture protection and cashback
  const hasNonStandardQuantity = useMemo(
    () =>
      state.wheelTireCombinations.some((combination: any) =>
        combination.tires.some((tire: any) => tire.quantity !== 4),
      ),
    [state.wheelTireCombinations],
  );

  return {
    // State
    wheelOptionsPrice,
    customerInfo: state.customerInfo,
    costSetting: state.costSetting,
    quoteByList: state.quoteByList,
    selectOptions,
    newQuoteMaker,
    wheelTireCombinations: state.wheelTireCombinations,
    hasNonStandardQuantity,

    // Handlers
    setCustomerInfo,
    setCostSetting,
    updateWheelTireDetail,
    updateWheelRemarks,
    handleSelectClear,
    handleSelectAllTires,
    handleClearAllTires,
  };
};

export default useProcFeeConfirm;
